import React, { useState } from "react";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Plus,
  Search,
  Sparkles,
  Trash2,
  User,
  X,
} from "lucide-react";
import {
  useArchiveScoutSearch,
  useCreateScoutSearch,
} from "../../hooks/queries/useScoutSearches";
import { runScoutSearchAgent } from "../../api/scoutSearchAgent";
import {
  extractScoutCriteria,
  summarizeCriteria,
} from "../../utils/extractScoutCriteria";
import { getFormattedDate } from "../../utils/formatDate";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
};

const NewSearchModal = ({ scoutId, onClose, onCreated }) => {
  const [brief, setBrief] = useState("");
  const [status, setStatus] = useState({ kind: "idle", message: "" });
  const [steps, setSteps] = useState([]);
  const createSearch = useCreateScoutSearch();

  // Guarda la búsqueda. `extracted` puede ser null: crear sin criterios es una
  // opción explícita del scout, no un fallback silencioso.
  const saveSearch = async (trimmed, extracted) => {
    setStatus({ kind: "loading", message: "Guardando búsqueda…" });

    const agentSummary = extracted?.shortlist
      ? {
          search_notes: extracted.search_notes ?? null,
          shortlist: extracted.shortlist.map((a) => ({
            athlete_id: a.athlete_id ?? a.id,
            full_name: a.full_name,
            rationale: a.rationale,
            score: a.score,
          })),
          generated_at: new Date().toISOString(),
        }
      : null;

    // scout_id se fuerza server-side; el payload conserva el mismo shape.
    const payload = {
      title: extracted?.title || trimmed.split(/\s+/).slice(0, 6).join(" "),
      recruiting_brief: trimmed,
      criteria: extracted?.criteria || null,
      criteria_extracted_at: extracted ? new Date().toISOString() : null,
      agent_summary: agentSummary,
    };

    let data = null;
    try {
      data = await createSearch.mutateAsync(payload);
    } catch (error) {
      setStatus({
        kind: "error",
        message: `No se pudo guardar: ${error.message}`,
      });
      return;
    }

    setStatus({
      kind: "success",
      message: extracted ? "Búsqueda creada." : "Búsqueda creada (sin criterios).",
    });
    setTimeout(() => onCreated?.(data?.id), 400);
  };

  const handleSubmit = async (forceWithoutCriteria = false) => {
    const trimmed = brief.trim();
    if (!trimmed) {
      setStatus({ kind: "error", message: "Escribe el brief antes de guardar." });
      return;
    }
    if (!scoutId) {
      setStatus({ kind: "error", message: "Scout no identificado." });
      return;
    }

    if (forceWithoutCriteria) {
      await saveSearch(trimmed, null);
      return;
    }

    setSteps([]);
    setStatus({ kind: "loading", message: "Interpretando el brief…" });

    // The agent checks its criteria against the real athlete pool before
    // committing, and streams what it's doing; the one-shot extractor is the
    // fallback when it fails.
    //
    // If BOTH fail we stop and ask, instead of quietly creating a search with no
    // criteria: that search matches every athlete, which looks like a working
    // result rather than a failure, and the scout has no way to tell.
    let extracted = null;
    try {
      extracted = await runScoutSearchAgent(trimmed, {
        onProgress: (line) => {
          setStatus({ kind: "loading", message: line });
          setSteps((prev) => (prev[prev.length - 1] === line ? prev : [...prev, line]));
        },
      });
    } catch {
      setStatus({ kind: "loading", message: "Reintentando extracción básica…" });
      try {
        extracted = await extractScoutCriteria(trimmed);
      } catch {
        setStatus({
          kind: "extraction_failed",
          message:
            "No pudimos procesar los criterios con IA (revisa tu conexión o el servicio de IA). Reintenta, o crea la búsqueda sin criterios para ver todos los atletas.",
        });
        return;
      }
    }

    await saveSearch(trimmed, extracted);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-t-2xl sm:rounded-xl bg-white p-5 sm:p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 transition-colors hover:bg-gray-100"
        >
          <X size={20} className="text-gray-700" />
        </button>

        <div className="mb-4 flex items-center gap-2">
          <Sparkles size={20} className="text-blue-900" />
          <h3 className="text-xl font-bold text-blue-900">Nueva búsqueda</h3>
        </div>
        <p className="mb-4 text-sm text-gray-600">
          Describe en lenguaje natural qué tipo de atleta buscas. Buscaremos
          atletas reales para ajustar los criterios antes de guardarlos.
        </p>

        <textarea
          value={brief}
          onChange={(event) => setBrief(event.target.value)}
          rows={5}
          autoFocus
          placeholder="Ej: Busco delantero sub-26 en Sudamérica, semi-profesional o más, con velocidad y definición en área."
          className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-600"
        />

        {steps.length > 0 && status.kind !== "error" && (
          <ol className="mt-3 space-y-1 border-l-2 border-blue-100 pl-3 text-xs text-gray-500">
            {steps.slice(0, -1).map((line, i) => (
              <li key={`${line}-${i}`}>{line}</li>
            ))}
          </ol>
        )}

        <div className="mt-3 min-h-[20px] text-xs">
          {status.kind === "loading" && (
            <span className="flex items-center gap-2 text-gray-600">
              <Loader2 className="h-3 w-3 animate-spin" />
              {status.message}
            </span>
          )}
          {status.kind === "success" && (
            <span className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 size={14} />
              {status.message}
            </span>
          )}
          {status.kind === "warning" && (
            <span className="flex items-center gap-2 text-amber-700">
              <AlertCircle size={14} />
              {status.message}
            </span>
          )}
          {status.kind === "extraction_failed" && (
            <span className="flex items-start gap-2 text-amber-700">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              {status.message}
            </span>
          )}
          {status.kind === "error" && (
            <span className="flex items-center gap-2 text-red-700">
              <AlertCircle size={14} />
              {status.message}
            </span>
          )}
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-100 px-5 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200"
          >
            Cancelar
          </button>
          {status.kind === "extraction_failed" && (
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              className="rounded-lg border border-blue-200 bg-blue-50 px-5 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
            >
              Crear sin criterios
            </button>
          )}
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={status.kind === "loading" || !brief.trim()}
            className="flex items-center gap-2 rounded-lg bg-blue-900 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status.kind === "loading" ? "Creando…" : "Crear búsqueda"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SearchCard = ({ search, onOpen, onArchive }) => {
  const summary = summarizeCriteria(search.criteria);
  const preview = (search.recruiting_brief || "").slice(0, 160);

  return (
    <button
      type="button"
      onClick={() => onOpen(search.id)}
      className="group relative w-full rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-all hover:border-blue-400 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="truncate text-base font-bold text-blue-900">
            {search.title || "Búsqueda sin título"}
          </h3>
          {preview && (
            <p className="mt-1 line-clamp-2 text-sm text-gray-600">{preview}</p>
          )}
          {summary && (
            <p className="mt-2 line-clamp-2 text-xs text-blue-800">{summary}</p>
          )}
          <p className="mt-3 text-[11px] text-gray-400">
            Actualizada {formatDate(search.updated_at || search.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onArchive(search.id);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                onArchive(search.id);
              }
            }}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
            aria-label="Archivar búsqueda"
          >
            <Trash2 size={16} />
          </span>
          <ChevronRight
            size={18}
            className="text-gray-300 transition-colors group-hover:text-blue-500"
          />
        </div>
      </div>
    </button>
  );
};

const SearchesList = ({
  scoutId,
  searches = [],
  displayName = "Scout",
  onNotificationsClick,
  onOpenSearch,
  onCreated,
}) => {
  const [showModal, setShowModal] = useState(false);
  const firstName = displayName?.split(" ")[0] || "Scout";
  const archiveSearch = useArchiveScoutSearch();

  const handleArchive = async (searchId) => {
    if (!searchId) return;
    if (!window.confirm("¿Archivar esta búsqueda? Podrás recrearla luego.")) return;
    try {
      await archiveSearch.mutateAsync(searchId);
    } catch (error) {
      window.alert(`No se pudo archivar: ${error.message}`);
      return;
    }
    onCreated?.(undefined);
  };

  return (
    <main className="flex-1 overflow-y-auto bg-gray-50">
      <div className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-gray-500">
              {getFormattedDate()}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">
              Mis búsquedas
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              {firstName}, gestiona tus briefs de scouting y abre el listado de
              atletas que mejor coincida con cada uno.
            </p>
          </div>

          <div className="hidden lg:flex items-center gap-3 text-blue-950">
            <Search size={19} />
            <button
              type="button"
              onClick={onNotificationsClick}
              className="p-1.5 hover:bg-white"
              aria-label="Notificaciones"
            >
              <Bell size={18} />
            </button>
            <User size={18} />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {searches.length} búsqueda{searches.length === 1 ? "" : "s"} activa
            {searches.length === 1 ? "" : "s"}
          </p>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
          >
            <Plus size={16} />
            Nueva búsqueda
          </button>
        </div>

        {searches.length === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
            <Sparkles size={36} className="text-gray-300" />
            <p className="mt-3 text-base font-semibold text-gray-700">
              Crea tu primera búsqueda
            </p>
            <p className="mt-1 max-w-md text-sm text-gray-500">
              Describe en lenguaje natural qué tipo de atleta buscas. Convertimos
              el brief en criterios estructurados y rankeamos los atletas
              automáticamente.
            </p>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="mt-4 flex items-center gap-2 rounded-lg bg-blue-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
            >
              <Plus size={16} />
              Nueva búsqueda
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {searches.map((search) => (
              <SearchCard
                key={search.id}
                search={search}
                onOpen={onOpenSearch}
                onArchive={handleArchive}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <NewSearchModal
          scoutId={scoutId}
          onClose={() => setShowModal(false)}
          onCreated={(newId) => {
            setShowModal(false);
            onCreated?.(newId);
            if (newId) onOpenSearch?.(newId);
          }}
        />
      )}
    </main>
  );
};

export default SearchesList;
